import React, { useEffect, useRef } from 'react';
import { useRelay } from '../../../hooks/useRelay';
import {
  applyAction, getBotAction, getPerfectBotAction, getValidActions,
} from '../../../games/van-life/van-life';
import type { VanLifeState, VanLifeAction } from '../../../games/van-life/van-life';
import { PARK_ADJACENCY } from '../../../games/van-life/van-life';
import type { RoomView } from '../../../types/room';
import VanLifeMap from './components/VanLifeMap';
import ParkInfoPanel from './components/ParkInfoPanel';
import MovementPanel from './components/MovementPanel';
import DiscardPanel from './components/DiscardPanel';
import TrickPanel from './components/TrickPanel';
import MatchResultPanel from './components/MatchResultPanel';

interface Props {
  slug: string;
  room: RoomView;
  myId: string;
  isLeader: boolean;
  initialState: VanLifeState | null;
  onFinish: () => void;
}

export default function VanLifeRoomGame({ slug, room, myId, isLeader, initialState, onFinish: _onFinish }: Props) {
  const { state, startRelay, sendAction } = useRelay<VanLifeState>(slug);
  const started = useRef(false);

  useEffect(() => {
    if (!isLeader || !initialState || started.current) return;
    started.current = true;
    startRelay('van-life', initialState);
  }, [isLeader, initialState, startRelay]);

  // Bot loop — leader fires one bot action per state tick
  useEffect(() => {
    if (!isLeader || !state) return;
    const botIds = new Set(
      room.participants.filter(p => p.type === 'bot').map(p => p.id)
    );
    if (botIds.size === 0) return;

    const strength = state.config.botStrength ?? 'medium';

    const getBotFn = (pid: string) => {
      if (strength === 'strong') return getPerfectBotAction(state, pid);
      if (strength === 'weak') {
        const valid = getValidActions(state, pid);
        return valid.length > 0 ? valid[Math.floor(Math.random() * valid.length)] : null;
      }
      return getBotAction(state, pid);
    };

    for (const player of state.players) {
      if (!botIds.has(player.id)) continue;
      const action = getBotFn(player.id);
      if (!action) continue;
      const next = applyAction(state, action);
      if (next !== state) { sendAction(action, next); break; }
    }
  }, [state, isLeader, room.participants, sendAction]);

  if (!state) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <p className="text-amber-200 text-lg" style={{ fontFamily: 'Georgia, serif' }}>Loading Van Life...</p>
      </div>
    );
  }

  const dispatch = (action: VanLifeAction) => sendAction(action, applyAction(state, action));

  const me = state.players.find(p => p.id === myId)!;
  const p1 = state.players.find(p => p.playerNumber === 1)!;
  const p2 = state.players.find(p => p.playerNumber === 2)!;
  const conqueredCount = state.parks.filter(p => p.conquered).length;

  // Movement phase — what parks can I move to?
  const movableParkIds: string[] = state.phase === 'movement' && !state.pendingMoves[myId]
    ? [me.currentParkId, ...(PARK_ADJACENCY[me.currentParkId] ?? [])]
    : [];

  const handleMapParkClick = (parkId: string) => {
    if (state.phase !== 'movement' || state.pendingMoves[myId] !== undefined) return;
    dispatch({ type: 'chooseMove', playerId: myId, targetParkId: parkId });
  };

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100" style={{ fontFamily: 'Georgia, serif' }}>

      {/* Top bar */}
      <div className="bg-gray-900 border-b border-gray-800 px-4 py-2 flex items-center gap-4 flex-wrap">
        <h1 className="text-lg font-bold text-amber-200">Van Life</h1>
        <span className="text-gray-500 text-sm">Match {state.matchNumber}/5</span>
        <span className="text-gray-500 text-sm">Parks {conqueredCount}/8</span>
      </div>

      {/* Body */}
      <div className="flex flex-col lg:flex-row gap-0 min-h-[calc(100vh-52px)]">

        {/* Left: Map */}
        <div className="lg:w-[45%] border-b lg:border-b-0 lg:border-r border-gray-800 p-3 bg-gray-950">
          <VanLifeMap
            parks={state.parks}
            players={state.players}
            myId={myId}
            movableParkIds={movableParkIds}
            onSelectPark={handleMapParkClick}
            tracker={state.match?.tracker}
            p1Name={p1.name}
            p2Name={p2.name}
            matchNumber={state.matchNumber}
          />
          {/* Park info panels below map */}
          <div className="mt-3 space-y-2">
            {state.players.map(player => {
              const park = state.parks.find(pk => pk.id === player.currentParkId)!;
              return <ParkInfoPanel key={player.id} park={park} player={player} isMe={player.id === myId} />;
            })}
          </div>
        </div>

        {/* Right: Game area */}
        <div className="lg:flex-1 p-4 flex flex-col gap-4">

          {state.phase === 'finished' && (
            <div className="flex-1 flex flex-col items-center justify-center gap-4">
              {state.gameWon ? (
                <>
                  <div className="text-6xl">★</div>
                  <h2 className="text-3xl font-bold text-yellow-400">You Win!</h2>
                  <p className="text-gray-400">All 8 Alaska National Parks conquered.</p>
                </>
              ) : (
                <>
                  <div className="text-5xl text-gray-600">✗</div>
                  <h2 className="text-2xl font-bold text-gray-400">Expedition Failed</h2>
                  <p className="text-gray-500">
                    {conqueredCount}/8 parks conquered in 5 matches.
                  </p>
                  <div className="text-sm text-gray-600">
                    Unconquered: {state.parks.filter(p => !p.conquered).map(p => p.name).join(', ')}
                  </div>
                </>
              )}
            </div>
          )}

          {state.phase === 'movement' && (
            <MovementPanel state={state} myId={myId} movableParkIds={movableParkIds} dispatch={dispatch} />
          )}

          {state.phase === 'trick-taking' && state.match?.subPhase === 'discard' && (
            <DiscardPanel state={state} myId={myId} dispatch={dispatch} />
          )}

          {state.phase === 'trick-taking' && state.match && state.match.subPhase !== 'discard' && (
            <TrickPanel state={state} myId={myId} dispatch={dispatch} />
          )}

          {state.phase === 'match-result' && state.match?.result && (
            <MatchResultPanel state={state} myId={myId} isLeader={isLeader} dispatch={dispatch} />
          )}

        </div>
      </div>
    </div>
  );
}
